/** @badge Custom — унифицированная стрелка-индикатор (поворот или смена chevron-right/down)
 *  @when   индикатор раскрытия/направления рядом с заголовком строки или секции
 *  @not    самостоятельная кнопка навигации — это IconButton; статичный значок — это Icon
 *  @breaks свой `transform` в style полностью перекрывает поворот — spread стоит последним
 */
"use client";

import { Icon } from "./Icon";
import { duration } from "@/lib/tokens/primitives";

type ChevronDirection = "down" | "right" | "left" | "up";
type ChevronSize = "xs" | "sm" | "md" | "lg";
type ChevronColor = "default" | "active" | "forced" | "accent" | "danger" | "success";

interface ChevronProps {
  /** Direction the chevron points when NOT expanded */
  direction?: ChevronDirection;
  /** When true, rotates 90° clockwise (down→right) or uses down icon */
  expanded?: boolean;
  size?: ChevronSize;
  color?: ChevronColor;
  /** Low opacity indicator style (for "viewable" hints) */
  subtle?: boolean;
  /** Override expanded color */
  expandedColor?: ChevronColor;
  style?: React.CSSProperties;
}

// Rotation degrees for each direction
const ROTATION: Record<ChevronDirection, number> = {
  down: 0, right: -90, up: 180, left: 90,
};

/**
 * Chevron - Unified expandable arrow component
 * 
 * Usage:
 * - Folder expand: <Chevron direction="right" expanded={open} />
 * - Dropdown:      <Chevron expanded={isOpen} />
 * - View hint:     <Chevron direction="right" subtle />
 */
export function Chevron({
  direction = "down",
  expanded = false,
  size = "sm",
  color = "default",
  subtle = false,
  expandedColor,
  style,
}: ChevronProps) {
  // Use chevron-down and rotate, OR switch icons for right→down
  const useIconSwitch = direction === "right";
  const iconName = useIconSwitch
    ? (expanded ? "chevron-down" : "chevron-right")
    : "chevron-down";

  // Calculate rotation (only for non-switching mode)
  const baseRotation = useIconSwitch ? 0 : ROTATION[direction];
  const expandedOffset = useIconSwitch ? 0 : 90;
  const rotation = expanded ? baseRotation + expandedOffset : baseRotation;

  const resolvedColor = expanded && expandedColor ? expandedColor : color;

  return (
    <Icon
      name={iconName}
      size={size}
      color={resolvedColor}
      style={{
        transform: `rotate(${rotation}deg)`,
        transition: `transform ${duration.normal}`,
        opacity: subtle ? 0.5 : 1,
        ...style,
      }}
    />
  );
}
